// src/contexts/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { User as FirebaseUser, onAuthStateChanged, getIdTokenResult } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, firestore } from '../config/firebase';
import { User } from '../types';

export type AdminTab = 'users' | 'updates' | 'worlds' | 'shop';

export interface AdminClaims {
    isAdmin: boolean;
    isSuperAdmin: boolean;
    adminTabs: AdminTab[];
}

const NO_ADMIN: AdminClaims = { isAdmin: false, isSuperAdmin: false, adminTabs: [] };

interface AuthContextType {
    currentUser: FirebaseUser | null;
    userData: User | null;
    loading: boolean;
    /**
     * Admin rights, read from the Firebase Auth ID token. These are set by a backend
     * (the setAdminPermissions callable) and cannot be altered by the client, which is
     * why security rules can trust them.
     */
    adminClaims: AdminClaims;
    /** Forces an ID token refresh. Call after admin rights change to avoid the ~1h wait. */
    refreshAdminClaims: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
    currentUser: null,
    userData: null,
    loading: true,
    adminClaims: NO_ADMIN,
    refreshAdminClaims: async () => {},
});

export const useAuth = () => useContext(AuthContext);

// Must match ADMIN_TABS in functions/src/admin.ts. It did not: `worlds` was added there
// when the tool shipped but not here, so this filter dropped it out of every ordinary
// admin's claims and the tool was visible only to a super admin, who bypasses the list.
const VALID_TABS: readonly string[] = ['users', 'updates', 'worlds', 'shop'];

function readClaims(token: { claims: Record<string, unknown> }): AdminClaims {
    const rawTabs = token.claims.adminTabs;
    return {
        isAdmin: token.claims.admin === true,
        isSuperAdmin: token.claims.superAdmin === true,
        adminTabs: Array.isArray(rawTabs)
            ? (rawTabs.filter((t): t is AdminTab => typeof t === 'string' && VALID_TABS.includes(t)))
            : [],
    };
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
    const [userData, setUserData] = useState<User | null>(null);
    const [adminClaims, setAdminClaims] = useState<AdminClaims>(NO_ADMIN);
    const [loading, setLoading] = useState(true);

    const refreshAdminClaims = useCallback(async () => {
        const user = auth.currentUser;
        if (!user) {
            setAdminClaims(NO_ADMIN);
            return;
        }
        const token = await getIdTokenResult(user, true);
        setAdminClaims(readClaims(token));
    }, []);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setCurrentUser(user);

            if (user) {
                const [userDoc, token] = await Promise.all([
                    getDoc(doc(firestore, 'users', user.uid)),
                    getIdTokenResult(user),
                ]);
                if (userDoc.exists()) {
                    setUserData(userDoc.data() as User);
                }
                setAdminClaims(readClaims(token));
            } else {
                setUserData(null);
                setAdminClaims(NO_ADMIN);
            }

            setLoading(false);
        });

        return unsubscribe;
    }, []);

    return (
        <AuthContext.Provider value={{ currentUser, userData, loading, adminClaims, refreshAdminClaims }}>
            {!loading && children}
        </AuthContext.Provider>
    );
};
